import { useState, useEffect } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  Title,
  ArcElement,
  BarElement,
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  Title,
  ArcElement,
  BarElement
);

const countries = [
  { name: "France", meyer: [2, 3, 2, 4, 3, 2, 3, 3] },
  { name: "Japon", meyer: [6, 7, 1, 8, 7, 6, 7, 8] },
  { name: "États-Unis", meyer: [1, 1, 2, 2, 1, 1, 2, 1] },
  { name: "Brésil", meyer: [7, 6, 3, 6, 5, 7, 6, 6] },
  { name: "Suède", meyer: [3, 2, 3, 2, 2, 3, 2, 2] },
  { name: "Suisse", meyer: [2, 3, 3, 3, 3, 2, 3, 3] },
  { name: "Canada", meyer: [2, 3, 2, 3, 3, 2, 3, 2] },
  { name: "Royaume-Uni", meyer: [2, 2, 2, 3, 2, 2, 3, 2] },
  { name: "Portugal", meyer: [6, 5, 3, 7, 6, 6, 6, 6] },
  { name: "Espagne", meyer: [4, 4, 3, 4, 4, 4, 4, 4] },
  { name: "Belgique", meyer: [3, 3, 3, 3, 3, 3, 3, 3] },
];

const fullLabels = [
  "Communication",
  "Feedback",
  "Persuasion",
  "Direction",
  "Décision",
  "Confiance",
  "Désaccord",
  "Temps"
];

const explanations = [
  ["Très direct", "Très indirect"],
  ["Direct", "Diplomatique"],
  ["Théorique", "Pratique"],
  ["Égalitaire", "Hiérarchique"],
  ["Consensus", "Top-down"],
  ["Orienté tâche", "Orienté relationnel"],
  ["Confrontation", "Évitement"],
  ["Flexible", "Linéaire"]
];

const getTooltipLabel = (context) => {
  const country = context.dataset.label;
  const index = context.dataIndex;
  const value = context.raw;
  const [min, max] = explanations[index];
  let interpretation = value < 3 ? min : value > 5 ? max : `entre ${min} et ${max}`;
  return `${country} – ${fullLabels[index]} : ${interpretation} (${value})`;
};

export default function CulturalComparison() {
  const [firstCountry, setFirstCountry] = useState(countries[0]);
  const [secondCountry, setSecondCountry] = useState(countries[1]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div style={{ padding: "1rem", maxWidth: "800px", margin: "auto", fontFamily: "Playfair Display, serif", color: "black" }}>
      <h4 style={{ fontFamily: "Mulish, sans-serif", textAlign: "center", fontSize: "1.25rem", marginBottom: "1.5rem", color: "#AF9646" }}>Sélectionnez deux pays à comparer</h4>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "1.5rem", marginBottom: "1.5rem", fontSize: isMobile ? "1.1rem" : "1.2rem" }}>
        <select style={{ fontSize: isMobile ? "1.1rem" : "1.2rem", padding: "0.5rem" }} value={firstCountry.name} onChange={(e) => setFirstCountry(countries.find(c => c.name === e.target.value))}>
          {countries.map(c => <option key={c.name}>{c.name}</option>)}
        </select>
        <select style={{ fontSize: isMobile ? "1.1rem" : "1.2rem", padding: "0.5rem" }} value={secondCountry.name} onChange={(e) => setSecondCountry(countries.find(c => c.name === e.target.value))}>
          {countries.map(c => <option key={c.name}>{c.name}</option>)}
        </select>
      </div>

      {!isMobile ? (
        <div style={{ height: "520px", marginTop: "2rem" }}>
          <Radar
            data={{
              labels: fullLabels,
              datasets: [
                {
                  label: firstCountry.name,
                  data: firstCountry.meyer,
                  backgroundColor: "rgba(175, 150, 70, 0.2)",
                  borderColor: "#AF9646",
                  borderWidth: 2,
                },
                {
                  label: secondCountry.name,
                  data: secondCountry.meyer,
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  borderColor: "black",
                  borderWidth: 2,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                tooltip: { callbacks: { label: getTooltipLabel } },
                legend: { position: "top" },
              },
              scales: {
                r: {
                  angleLines: { color: "#ccc" },
                  grid: { color: "#e5e5e5" },
                  pointLabels: { font: { size: 12 }, color: "#333" },
                  ticks: { min: 0, max: 8, stepSize: 1, color: "#666" },
                }
              }
            }}
          />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {fullLabels.map((label, i) => (
            <div key={label} style={{ background: "#f7f7f7", padding: "1rem", borderRadius: "10px" }}>
              <h3 style={{ textAlign: "center", marginBottom: "0.5rem", fontSize: "1rem" }}>{label}</h3>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1rem", marginBottom: "0.25rem" }}>
                <span>{explanations[i][0]}</span>
                <span>{explanations[i][1]}</span>
              </div>
              <div style={{ background: "#ddd", borderRadius: "4px", marginBottom: "4px", height: "6px" }}>
                <div style={{ width: `${(firstCountry.meyer[i] / 8) * 100}%`, backgroundColor: "#AF9646", height: "6px", borderRadius: "4px" }}></div>
              </div>
              <div style={{ background: "#ddd", borderRadius: "4px", height: "6px" }}>
                <div style={{ width: `${(secondCountry.meyer[i] / 8) * 100}%`, backgroundColor: "#000", height: "6px", borderRadius: "4px" }}></div>
              </div>
              <div style={{ textAlign: "center", fontSize: "1rem", marginTop: "0.75rem" }}>
                <span style={{ color: "#AF9646", fontWeight: "bold" }}>{firstCountry.name} : {firstCountry.meyer[i]} / 8</span>
                &nbsp;|&nbsp;
                <span style={{ color: "#000", fontWeight: "bold" }}>{secondCountry.name} : {secondCountry.meyer[i]} / 8</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
